/**
 * 日期工具类
 * @description 用于日期转化、日期格式化等操作
 * @author AndyZheng
 * @date 2020.08.25
 * @version v1.0
 *
 * ======================================
 * @description 添加isToday()
 * @author Steven
 * @date 2020.08.26
 * @version v1.1
 *
 */

// dateFormat("YYYY-mm-dd HH:MM", date)
export default function dateFormat(fmt, date) {
  let ret;
  const opt = {
    'Y+': date.getFullYear().toString(), // 年
    'm+': (date.getMonth() + 1).toString(), // 月
    'd+': date.getDate().toString(), // 日
    'H+': date.getHours().toString(), // 时
    'M+': date.getMinutes().toString(), // 分
    'S+': date.getSeconds().toString() // 秒
    // 有其他格式化字符需求可以继续添加，必须转化成字符串
  };
  for (let k in opt) {
    ret = new RegExp('(' + k + ')').exec(fmt);
    if (ret) {
      fmt = fmt.replace(ret[1], ret[1].length == 1 ? opt[k] : opt[k].padStart(ret[1].length, '0'));
    }
  }
  return fmt;
}

/**
 * 时间戳转日期对象
 * @param {Object} timestamp
 */
export function timestampToDate(timestamp) {
  if (timestamp) {
    if (timestamp instanceof Date) {
      return timestamp;
    }
    if (typeof timestamp == 'string' && timestamp.indexOf('-') != -1) {
      // timestamp = timestamp.replace(/\-/g, '/')
      timestamp = timestamp.replace(/\-/g, '/').replace('T', ' ');
    }
    return new Date(timestamp);
  }
  return timestamp;
}

/**
 * 日期对象转时间戳
 * @param {Object} date
 */
export function dateToTimestamp(date) {
  if (date && date instanceof Date) {
    return date.getTime();
  }
  return date;
}

/**
 * 月(M)、日(d)、小时(h)、分(m)、秒(s)、季度(q) 可以用 1-2 个占位符，
 * 年(y)可以用 1-4 个占位符，毫秒(S)只能用 1 个占位符(是 1-3 位的数字)
 * 例子：
 * (new Date()).Format("yyyy-MM-dd hh:mm:ss.S") ==> 2006-07-02 08:09:04.423
 * (new Date()).Format("yyyy-M-d h:m:s.S")   ==> 2006-7-2 8:9:4.18
 * @param {Object} date
 * @param {Object} fmt
 */
export function formatDate(date, fmt = 'yyyy-MM-dd') {
  if (!date) {
    return null;
  }
  if (date && !(date instanceof Date)) {
    date = timestampToDate(date);
  }
  var o = {
    'M+': date.getMonth() + 1, //月份
    'd+': date.getDate(), //日
    'D+': date.getDay(), //周
    'h+': date.getHours(), //小时
    'm+': date.getMinutes(), //分
    's+': date.getSeconds(), //秒
    'q+': Math.floor((date.getMonth() + 3) / 3), //季度
    S: date.getMilliseconds() //毫秒
  };

  if (/(y+)/.test(fmt)) fmt = fmt.replace(RegExp.$1, (date.getFullYear() + '').substr(4 - RegExp.$1.length));
  for (var k in o) {
    if (new RegExp('(' + k + ')').test(fmt)) {
      fmt = fmt.replace(RegExp.$1, RegExp.$1.length == 1 ? o[k] : ('00' + o[k]).substr(('' + o[k]).length));
    }
  }

  return fmt;
}

/**
 * 根据日期获取星期
 * @param {Object} date
 */
export function getDay(date, myapp) {
  if (date && !(date instanceof Date)) {
    date = timestampToDate(date);
  }
  let day = date.getDay();
  if (!myapp) {
    switch (day) {
      case 0:
        return '周日';
      case 1:
        return '周一';
      case 2:
        return '周二';
      case 3:
        return '周三';
      case 4:
        return '周四';
      case 5:
        return '周五';
      case 6:
        return '周六';
      default:
        return '周一';
    }
  } else {
    switch (day) {
      case 0:
        return myapp.$t('common_week_sunday');
      case 1:
        return myapp.$t('common_week_monday');
      case 2:
        return myapp.$t('common_week_tuesday');
      case 3:
        return myapp.$t('common_week_wednesday');
      case 4:
        return myapp.$t('common_week_thursday');
      case 5:
        return myapp.$t('common_week_friday');
      case 6:
        return myapp.$t('common_week_saturday');
      default:
        return myapp.$t('common_week_sunday');
    }
  }
}

/**
 * 是否同一天对比，仅年月日
 * @param {Object} date1
 * @param {Object} date2
 * @param {Object} type
 */
export function dateEquals(date1, date2, type) {
  if (date1 && !(date1 instanceof Date)) {
    date1 = timestampToDate(date1);
  }
  if (date2 && !(date2 instanceof Date)) {
    date2 = timestampToDate(date2);
  }
  let fmt = 'yyyy/MM/dd';
  if (type == 'year') {
    fmt = 'yyyy';
  } else if (type == 'month') {
    fmt = 'yyyy/MM';
  }
  let formattedDate1 = formatDate(date1, fmt);
  let formattedDate2 = formatDate(date2, fmt);

  return formattedDate1 == formattedDate2 || date1 == date2;
}

/**
 * 是否是今天对比，仅年月日
 * count 不为空时，则为是否是今天 + count 的判定
 * @param {Object} date
 * @param {Number} count
 */
export function isToday(date, count) {
  let targetDate = new Date();
  if (count) {
    targetDate = dateAdd(targetDate, count);
  }
  return dateEquals(date, targetDate);
}

/**
 * 日期加减
 * @param {Object} date
 * @param {Number} count
 */
export function dateAdd(date, count) {
  return timeAdd(count, date, 'date');
}

/**
 * 时间加减
 * @param {Number} count
 * @param {Object} date
 * @param {String} type 转换天数还是月数，后续可扩展年数和小时数
 */
export function timeAdd(count, date = new Date(), type = 'date') {
  if (!date) {
    return null;
  }
  let types = {
    date: 'Date',
    mouth: 'Month'
  };
  if (!types[type]) {
    type = 'date';
  }
  date = timestampToDate(date);
  let addedDate = new Date(date);
  let setMethod = 'set' + types[type];
  let getMethod = 'get' + types[type];
  addedDate[setMethod](date[getMethod]() + count);
  return addedDate;
}

/**
 * 分钟加减
 * @param {Object} date
 * @param {Number} count
 */
export function minuteAdd(date, count) {
  if (!date) {
    return null;
  }
  if (typeof date != 'number') {
    date = dateToTimestamp(date);
  }
  date += 60 * 1000 * count;
  date = timestampToDate(date);
  return date;
}

export function minuteDifference(date1, date2) {
  var dateSpan, tempDate, minute;
  if (date1 && !(date1 instanceof Date)) {
    date1 = timestampToDate(date1);
  }
  if (date2 && !(date2 instanceof Date)) {
    date2 = timestampToDate(date2);
  }
  dateSpan = date2 - date1;
  dateSpan = Math.abs(dateSpan);
  minute = Math.floor(dateSpan / (60 * 1000));
  return minute;
}



// WEBPACK FOOTER //
// ./src/utils/date-util.js